// draft/DraftIssueSolver.jsx
// Stripped-down issue coach for no-auth draft sessions.
// Uses the public /drafts/:guid/chat endpoint; no SSE, no auth headers.
import React, { Component, createRef } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { TypingIndicator } from '../components/Icon';
import IssueReadOnlyCard from '../dashboard/components/IssueReadOnlyCard';
import { urlMap } from '../../utils';

/**
 * Props:
 *  - guid          : string   — draft GUID
 *  - issue         : object   — seeded structural issue (title, description, suggestion, severity)
 *  - onClaimAccount: () => void
 */
export default class DraftIssueSolver extends Component {
  textareaRef = createRef();

  state = {
    messages: [],
    input: '',
    sending: false,
    typing: false,
    seedsUsed: 0,
    limitReached: false,
  };

  componentDidUpdate(prevProps, prevState) {
    // Keep focus on the composer after a message finishes sending
    if (prevState.sending && !this.state.sending) {
      this.textareaRef.current?.focus();
    }
  }

  // ── input ──

  onChange = (e) => {
    this.setState({ input: e.target.value }, () => {
      const el = this.textareaRef.current;
      if (el) {
        el.style.height = 'auto';
        el.style.height = `${el.scrollHeight}px`;
      }
    });
  };

  onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      this.handleSend();
    }
  };

  // ── send ──

  handleSend = async () => {
    const { guid } = this.props;
    const { input, limitReached } = this.state;
    const text = input.trim();
    if (!text || limitReached) return;

    const meId = `me-${Date.now()}`;
    const replyId = `them-${Date.now() + 1}`;

    this.setState((s) => ({
      input: '',
      sending: true,
      typing: true,
      messages: [...s.messages, { id: meId, from: 'me', text }],
    }));

    try {
      const res = await fetch(urlMap.onboardingChat(guid), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: text }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();

      if (data.limitReached) {
        this.setState((s) => ({
          sending: false,
          typing: false,
          limitReached: true,
          seedsUsed: data.seedsUsed ?? s.seedsUsed,
          messages: [
            ...s.messages,
            {
              id: replyId,
              from: 'them',
              text: "You've used your free coaching budget. Create a free account to keep working with the coach on this issue.",
              isLimit: true,
            },
          ],
        }));
        return;
      }

      this.setState((s) => ({
        sending: false,
        typing: false,
        seedsUsed: data.seedsUsed ?? s.seedsUsed + 1,
        messages: [
          ...s.messages,
          { id: replyId, from: 'them', text: data.reply || '(no response)' },
        ],
      }));
    } catch (err) {
      this.setState((s) => ({
        sending: false,
        typing: false,
        messages: [
          ...s.messages,
          { id: replyId, from: 'them', text: `⚠️ ${err.message || 'Failed to send'}` },
        ],
      }));
    }
  };

  // ── render ──

  render() {
    const { issue, onClaimAccount } = this.props;
    const { messages, input, sending, typing, limitReached } = this.state;

    const placeholder = limitReached
      ? 'Create an account to continue…'
      : sending
      ? 'Thinking…'
      : issue
      ? 'Ask the coach about this issue…'
      : 'Ask the coach anything about your draft…';

    return (
      <div className="coach-scroll">
        <aside className="coach-panel tasks-coach-panel" aria-label="Onboarding coach">

          {/* Header */}
          <div className="coach-header tasks-coach-header">
            <span className="tasks-coach-title">storyclub coach</span>
            {this.props.onClose && (
              <button
                type="button"
                className="coach-close-btn"
                onClick={this.props.onClose}
                aria-label="Close coach"
                title="Close"
              >
                ×
              </button>
            )}
          </div>

          {/* Seeded issue */}
          {issue && (
            <div className="tasks-coach-context">
              <div className="tasks-coach-context-list">
                <IssueReadOnlyCard
                  issue={issue}
                  defaultExpanded={true}
                  onStartFlow={null}
                />
              </div>
            </div>
          )}

          {/* Chat messages */}
          <div className="coach-messages">
            {messages.map((m) => (
              <div key={m.id} className={`coach-bubble ${m.from === 'me' ? 'me' : 'them'}`}>
                <ReactMarkdown className="coach-markdown" remarkPlugins={[remarkGfm]}>
                  {m.text}
                </ReactMarkdown>
                {m.isLimit && onClaimAccount && (
                  <button
                    type="button"
                    className="btn primary onboarding-coach-claim-btn"
                    onClick={onClaimAccount}
                  >
                    Create free account
                  </button>
                )}
              </div>
            ))}
            {typing && <TypingIndicator />}
          </div>

          {/* Chat input */}
          <div className="coach-input">
            <textarea
              ref={this.textareaRef}
              rows={1}
              placeholder={placeholder}
              value={input}
              disabled={sending || limitReached}
              onChange={this.onChange}
              onKeyDown={this.onKeyDown}
              className="coach-textarea"
            />
            <button
              type="button"
              className="btn primary"
              disabled={sending || !input.trim() || limitReached}
              onClick={this.handleSend}
            >
              Send <span className="seed-price-label">1 fig seed</span>
            </button>
          </div>

        </aside>
      </div>
    );
  }
}
